import { isNil } from "lodash";
import { createStore } from "zustand/vanilla";
import { preloadRepo } from "./preload";
import { gameRepo } from "./game";
import { TranslationUtils } from "@modules/src/utils/TranslationUtils";
import { ModulesManager } from "@modules/src/managers/ModulesManager";
import { TranslationsModule } from "@modules/src/modules/TranslationsModule";

export const FALLBACK_LANGUAGE = "en";

type TKeyValue = Record<string, string>;
type TTranslate = Record<string, string>;
type TLang = string;

export interface ITranslationRepo {
  currentLanguage: null | TLang;
  currentCountry: null | string;
  translate: (key: string, params?: Record<string, string>) => string;
  t: (key: string) => string;

  loadTranslate: (lang?: TLang) => Promise<void>;
  translations: Record<TLang, TTranslate>;

  setUrls: (common?: TKeyValue, standard?: TKeyValue, vendor?: TKeyValue) => void;
  urls: {
    common: TKeyValue;
    default: TKeyValue;
    vendor: TKeyValue;
  };

  loadFallback: () => Promise<void>;
  fallback: TTranslate;

  loadDynamicVendor: (url: null | string) => Promise<void>;
  dynamicVendor: Record<TLang, TTranslate>;
}

export const translationRepo = createStore<ITranslationRepo>((set, get) => {
  const loadTranslationGroup = async (_lang: TLang) => ({} as TTranslate);

  const loadFallback = async () => {
    set({ fallback: {} });
  };

  const loadDynamicVendor = async (url: null | string) => {
    void url;
  };

  const loadTranslate = async (lang = FALLBACK_LANGUAGE) => {
    const { translations } = get();

    console.log("[translationRepo] loadTranslate requested", {
      lang,
      currentLanguage: get().currentLanguage,
      availableTranslations: Object.keys(translations),
    });

    try {
      const translationsModule = ModulesManager.getModule(
        "translations"
      ) as TranslationsModule;

      console.log("[translationRepo] translations module", {
        exists: !!translationsModule,
        moduleLocale: (translationsModule as any)?._locale,
      });

      await translationsModule?.setLocale?.(lang);

      console.log("[translationRepo] setLocale completed", {
        lang,
        moduleLocale: (translationsModule as any)?._locale,
      });
    } catch (error) {
      console.warn("failed to switch translation locale", error);
    }

    set({
      currentLanguage: lang,
      translations: {
        ...translations,
        [lang]: {
          ...(translations[lang] || {}),
        },
      },
    });
  };

  const translate = (key = "", tParams: Record<string, string> = {}): any => {
    let phrase: string;
    try {
      phrase = TranslationUtils.translate(key);
    } catch {
      return "";
    }

    const params = Object.entries(tParams);
    if (!phrase || params.length === 0) {
      return phrase;
    }

    params.forEach(([pKey, pValue]) => {
      phrase = phrase
        .replace(`{${pKey}}`, !isNil(pValue) ? pValue : "")
        .replace(`{{${pKey}}}`, !isNil(pValue) ? pValue : "");
    });

    return phrase;
  };

  return {
    currentLanguage: FALLBACK_LANGUAGE,
    currentCountry: null,
    translations: {},
    translate: translate,
    t: translate,
    setUrls: (common = {}, standard = {}, vendor = {}) => {
      set({
        urls: {
          common,
          default: standard,
          vendor,
        },
      });
    },
    urls: {
      common: {},
      default: {},
      vendor: {},
    },
    fallback: {},
    dynamicVendor: {},
    loadFallback,
    loadTranslate,
    loadDynamicVendor,
  };
});

export const t = translationRepo.getState().t;

translationRepo.subscribe(({ currentLanguage }, prev) => {
  if (currentLanguage === prev.currentLanguage) return;

  console.log("[translationRepo] currentLanguage changed", {
    from: prev.currentLanguage,
    to: currentLanguage,
  });

  translationRepo.getState().loadTranslate(currentLanguage as string);
});

export const preloadTranslationData = async () => {
  const { setProgress, finish } = preloadRepo.getState();
  try {
    const {
      country = null,
      language = FALLBACK_LANGUAGE,
    } = gameRepo.getState();

    console.log("[translationRepo] preloadTranslationData", {
      country,
      language,
    });

    setProgress("translations", 90);

    translationRepo.setState({
      currentLanguage: language,
      currentCountry: country,
    });
  } catch (error) {
    console.log("error on loading translations", error);
  } finally {
    setProgress("translations", 100);
    finish("translations");
  }
};
